/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';

export const ORACLE_STATE_DISCRIMINATOR = new Uint8Array([
  97, 156, 157, 189, 194, 73, 8, 15,
]);

export function getOracleStateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    ORACLE_STATE_DISCRIMINATOR,
  );
}

export type OracleState = {
  discriminator: ReadonlyUint8Array;
  /** The authority that can finalize this oracle. */
  oracleAuthority: Address;
  /** Optional quote mint for validation (Pubkey::default() if not used). */
  quoteMint: Address;
  /** Whether the oracle has been finalized with a winner. */
  isFinalized: boolean;
  /** The winning mint (only valid if is_finalized == true). */
  winningMint: Address;
  /** Nonce used in PDA derivation. */
  nonce: bigint;
  /** PDA bump seed. */
  bump: number;
  /** Reserved for future use. */
  reserved: ReadonlyUint8Array;
};

export type OracleStateArgs = {
  /** The authority that can finalize this oracle. */
  oracleAuthority: Address;
  /** Optional quote mint for validation (Pubkey::default() if not used). */
  quoteMint: Address;
  /** Whether the oracle has been finalized with a winner. */
  isFinalized: boolean;
  /** The winning mint (only valid if is_finalized == true). */
  winningMint: Address;
  /** Nonce used in PDA derivation. */
  nonce: number | bigint;
  /** PDA bump seed. */
  bump: number;
  /** Reserved for future use. */
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link OracleStateArgs} account data. */
export function getOracleStateEncoder(): FixedSizeEncoder<OracleStateArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['oracleAuthority', getAddressEncoder()],
      ['quoteMint', getAddressEncoder()],
      ['isFinalized', getBooleanEncoder()],
      ['winningMint', getAddressEncoder()],
      ['nonce', getU64Encoder()],
      ['bump', getU8Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 31)],
    ]),
    (value) => ({ ...value, discriminator: ORACLE_STATE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link OracleState} account data. */
export function getOracleStateDecoder(): FixedSizeDecoder<OracleState> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['oracleAuthority', getAddressDecoder()],
    ['quoteMint', getAddressDecoder()],
    ['isFinalized', getBooleanDecoder()],
    ['winningMint', getAddressDecoder()],
    ['nonce', getU64Decoder()],
    ['bump', getU8Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 31)],
  ]);
}

/** Gets the codec for {@link OracleState} account data. */
export function getOracleStateCodec(): FixedSizeCodec<
  OracleStateArgs,
  OracleState
> {
  return combineCodec(getOracleStateEncoder(), getOracleStateDecoder());
}

export function decodeOracleState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<OracleState, TAddress>;
export function decodeOracleState<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<OracleState, TAddress>;
export function decodeOracleState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<OracleState, TAddress> | MaybeAccount<OracleState, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getOracleStateDecoder(),
  );
}

export async function fetchOracleState<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<OracleState, TAddress>> {
  const maybeAccount = await fetchMaybeOracleState(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeOracleState<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<OracleState, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeOracleState(maybeAccount);
}

export async function fetchAllOracleState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<OracleState>[]> {
  const maybeAccounts = await fetchAllMaybeOracleState(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeOracleState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<OracleState>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeOracleState(maybeAccount));
}

export function getOracleStateSize(): number {
  return 145;
}
